import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Download,
  ChevronRight,
  Shield,
  Info,
  Printer,
  Loader2,
  Trophy,
  Medal,
  Award
} from 'lucide-react';
import AssessmentLayout from '../../components/assessment/AssessmentLayout';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface ModuleScore {
  id: string;
  number: number;
  name_no: string;
  description?: string;
  rationale?: string;
  score: number;
  rank: number;
}

export default function AssessmentModuleRecommendation() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const [moduleScores, setModuleScores] = useState<ModuleScore[] | null>(null);
  const [loadingScores, setLoadingScores] = useState(true);

  useEffect(() => {
    // Get assessment ID from sessionStorage
    const storedAssessmentId = sessionStorage.getItem('currentAssessmentId');
    if (!storedAssessmentId) {
      navigate('/assessment/start');
      return;
    }
    setAssessmentId(storedAssessmentId);
  }, [navigate]);

  // Calculate module scores when assessmentId is available
  useEffect(() => {
    if (!assessmentId) return;

    const calculateScores = async () => {
      setLoadingScores(true);
      try {
        // Fetch the assessment's symptoms with their intensities
        const { data: assessmentSymptoms, error: sympError } = await supabase
          .from('assessment_symptoms')
          .select('symptom_id, intensity')
          .eq('assessment_id', assessmentId);

        if (sympError) throw sympError;
        if (!assessmentSymptoms || assessmentSymptoms.length === 0) {
          setModuleScores([]);
          setLoadingScores(false);
          return;
        }

        const symptomIds = assessmentSymptoms.map((s: any) => s.symptom_id);

        // Fetch influence rules for these symptoms
        const { data: rules, error: rulesError } = await supabase
          .from('influence_rules')
          .select('symptom_id, approach_id, weight')
          .in('symptom_id', symptomIds);

        if (rulesError) throw rulesError;

        // Fetch all approaches
        const { data: approaches, error: appError } = await supabase
          .from('approaches')
          .select('id, number, name_no, description, rationale')
          .eq('status', 'published')
          .order('number', { ascending: true });

        if (appError) throw appError;

        // Build intensity map: symptomId -> intensity
        const intensityMap: Record<string, number> = {};
        for (const as of assessmentSymptoms) {
          intensityMap[as.symptom_id] = as.intensity;
        }

        // Calculate scores per approach
        const scoreMap: Record<string, number> = {};
        for (const rule of (rules || [])) {
          const intensity = intensityMap[rule.symptom_id] || 0;
          const contribution = (intensity / 100) * (rule.weight || 1);
          scoreMap[rule.approach_id] = (scoreMap[rule.approach_id] || 0) + contribution;
        }

        // Build sorted module scores
        const scored: ModuleScore[] = (approaches || []).map((app: any) => ({
          id: app.id,
          number: app.number,
          name_no: app.name_no,
          description: app.description,
          rationale: app.rationale,
          score: Math.round(scoreMap[app.id] || 0),
          rank: 0,
        }));

        // Sort by score descending
        scored.sort((a, b) => b.score - a.score);

        // Assign ranks
        scored.forEach((m, i) => { m.rank = i + 1; });

        setModuleScores(scored);
      } catch (error) {
        console.error('Error calculating module scores:', error);
        setModuleScores([]);
      } finally {
        setLoadingScores(false);
      }
    };

    calculateScores();
  }, [assessmentId]);

  const getRankIcon = (rank: number) => {
    if (rank === 1) return Trophy;
    if (rank === 2) return Medal;
    if (rank === 3) return Award;
    return Shield;
  };

  const getRankColor = (rank: number) => {
    if (rank === 1) return 'text-yellow-500 dark:text-yellow-400';
    if (rank === 2) return 'text-gray-400 dark:text-gray-300';
    if (rank === 3) return 'text-orange-600 dark:text-orange-400';
    return 'text-blue-600 dark:text-blue-400';
  };

  const handleBack = () => {
    navigate('/assessment/symptoms');
  };

  const handleDownload = () => {
    if (!moduleScores || moduleScores.length === 0) return;

    // Generate and download work document
    const workDocument = generateWorkDocument();
    const blob = new Blob([workDocument], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `arbeidsdokument_${moduleScores[0].name_no.replace(' ', '_')}_${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Helper function to strip HTML tags
  const stripHtml = (html: string) => {
    return html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/\n\s*\n/g, '\n\n')
      .trim();
  };

  const generateWorkDocument = () => {
    if (!moduleScores || moduleScores.length === 0) return '';

    const topModules = moduleScores.slice(0, 2);

    return `ARBEIDSDOKUMENT - HELSEVURDERING
=====================================

Dato: ${new Date().toLocaleDateString('nb-NO')}

ANBEFALTE MODULER (Topp 2):

${topModules.map((module, idx) => `
${idx + 1}. ${module.name_no} (Score: ${module.score})
${module.description ? stripHtml(module.description) : ''}
${module.rationale ? '\n' + stripHtml(module.rationale) : ''}
`).join('\n')}

FULLSTENDIG RANGERING:
${moduleScores.map((m, idx) => `${idx + 1}. ${m.name_no}: ${m.score} poeng`).join('\n')}

=====================================
Dette dokumentet er generert av Helsenøkkelen Assessment System
`;
  };

  const handleContinueToReport = async () => {
    if (!assessmentId || !moduleScores || moduleScores.length === 0) {
      alert('Mangler nødvendig data. Prøv igjen.');
      return;
    }

    const topModule = moduleScores[0];

    try {
      // Store module recommendation for report first (before any async operations)
      sessionStorage.setItem('moduleRecommendation', JSON.stringify({
        module: topModule.name_no,
        score: topModule.score,
        riskLevel: topModule.score >= 150 ? 'high' : topModule.score >= 75 ? 'medium' : 'low',
        topModules: moduleScores.slice(0, 2)
      }));

      // Complete assessment in Supabase
      if (profile) {
        try {
          await supabase
            .from('assessments')
            .update({
              status: 'completed',
              recommended_module: topModule.name_no,
              risk_level: topModule.score >= 150 ? 'high' : topModule.score >= 75 ? 'medium' : 'low',
              completed_at: new Date().toISOString(),
            })
            .eq('id', assessmentId);
        } catch (error) {
          console.error('Error completing assessment:', error);
          // Continue anyway - we've already saved to sessionStorage
        }
      }

      // Record anonymous statistics
      try {
        const topThree = moduleScores.slice(0, 3).map(m => m.number);
        const hasCritical = topThree.some(n => n === 8 || n === 9);
        await supabase
          .from('assessment_stats')
          .insert({
            approach_number: topModule.number,
            top_three_approaches: topThree,
            has_critical_risk: hasCritical,
          });
      } catch (error) {
        console.error('Error recording statistics:', error);
        // Continue anyway - statistics are not critical
      }

      // Navigate to report page
      navigate('/assessment/report');
    } catch (error) {
      console.error('Error:', error);
      alert('Kunne ikke fullføre vurderingen. Prøv igjen.');
    }
  };

  if (loadingScores) {
    return (
      <AssessmentLayout
        title="Helseavdelingens anbefalte modul"
        subtitle="Analyserer vurdering..."
        backTo="/assessment/symptoms"
        currentStep={5}
      >
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-400" />
        </div>
      </AssessmentLayout>
    );
  }

  if (!moduleScores || moduleScores.length === 0) {
    return (
      <AssessmentLayout
        title="Helseavdelingens anbefalte modul"
        subtitle="Ingen resultater"
        backTo="/assessment/symptoms"
        currentStep={5}
      >
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-8 text-center">
          <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Ingen moduler å anbefale
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Det ble ikke funnet nok symptomdata til å beregne en modulanbefaling.
            Gå tilbake og velg symptomer med alvorlighetsgrad.
          </p>
          <button
            onClick={() => navigate('/assessment/symptoms')}
            className="px-6 py-3 bg-blue-600 dark:bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
          >
            Tilbake til symptomer
          </button>
        </div>
      </AssessmentLayout>
    );
  }

  const topModule = moduleScores[0];

  // Sjekk om førsteplass-modulen er kritisk (modul 8 eller 9)
  const isTopModuleCritical = topModule &&
    (topModule.number === 8 || topModule.number === 9) &&
    topModule.score > 0;

  // KRITISK header vises KUN hvis kritisk modul er på førsteplass
  const hasCriticalRisk = isTopModuleCritical;

  // For badges: sjekk om kritiske moduler finnes i topp 3
  const top3 = moduleScores.slice(0, 3);
  const hasSuicidalRisk = top3.some(m => m.number === 9 && m.score > 0);
  const hasViolenceRisk = top3.some(m => m.number === 8 && m.score > 0);

  return (
    <AssessmentLayout
      title="Helseavdelingens anbefalte modul"
      subtitle="Basert på din vurdering - rangering av alle moduler"
      backTo="/assessment/symptoms"
      currentStep={5}
    >
      {/* Ranking Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 mb-6 overflow-hidden">
        <div className={`p-6 ${hasCriticalRisk
          ? 'bg-gradient-to-r from-red-600 to-red-700 dark:from-red-700 dark:to-red-800'
          : 'bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800'}`}>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Trophy className="h-7 w-7" />
            Modulrangering
            {hasCriticalRisk && (
              <span className="ml-2 px-3 py-1 bg-white text-red-600 text-sm font-bold rounded-full animate-pulse">
                KRITISK
              </span>
            )}
          </h2>
          <p className="text-blue-100 mt-1">
            {hasCriticalRisk
              ? 'ADVARSEL: Kritisk modul detektert i topp 3'
              : 'De tre øverste modulene rangert etter score basert på valgte symptomer'}
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Prioritet
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Nummer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Navn
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Score
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {moduleScores.slice(0, 3).map((module, index) => {
                const RankIcon = getRankIcon(module.rank);
                const rankColor = getRankColor(module.rank);
                const isCritical = index < 3 && (module.number === 8 || module.number === 9) && module.score > 0;
                const isSuicidal = module.number === 9 && module.score > 0;
                const isViolence = module.number === 8 && module.score > 0;

                return (
                  <tr
                    key={module.id}
                    className={`
                      ${isCritical
                        ? 'bg-red-50 dark:bg-red-900/20 border-l-4 border-red-600 dark:border-red-500 font-bold'
                        : index < 2
                          ? 'bg-blue-50 dark:bg-blue-900/20 font-semibold'
                          : index === 2
                            ? 'bg-orange-50 dark:bg-orange-900/20 font-semibold'
                            : 'bg-white dark:bg-gray-800'}
                      hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors
                    `}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {isCritical ? (
                          <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
                        ) : (
                          <RankIcon className={`h-5 w-5 ${rankColor}`} />
                        )}
                        <span className={isCritical ? 'text-red-900 dark:text-red-100 font-bold' : 'text-gray-900 dark:text-white'}>
                          {module.rank}
                        </span>
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isCritical ? 'text-red-900 dark:text-red-100' : 'text-gray-900 dark:text-white'}`}>
                      {module.number}
                    </td>
                    <td className={`px-6 py-4 ${isCritical ? 'text-red-900 dark:text-red-100' : 'text-gray-900 dark:text-white'}`}>
                      <div className="flex items-center gap-2">
                        {module.name_no}
                        {isSuicidal && (
                          <span className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded animate-pulse">
                            SUICIDALFARE
                          </span>
                        )}
                        {isViolence && (
                          <span className="px-2 py-1 bg-orange-600 text-white text-xs font-bold rounded animate-pulse">
                            VOLDSRISIKO
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className={`
                        inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold
                        ${isCritical
                          ? 'bg-red-600 dark:bg-red-500 text-white'
                          : index === 0
                            ? 'bg-yellow-500 dark:bg-yellow-400 text-white'
                            : index === 1
                              ? 'bg-gray-400 dark:bg-gray-300 text-white'
                              : index === 2
                                ? 'bg-orange-600 dark:bg-orange-500 text-white'
                                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}
                      `}>
                        {module.score}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Module Display */}
      {topModule && (() => {
        const RankIcon = getRankIcon(topModule.rank);
        const rankColor = getRankColor(topModule.rank);

        return (
          <div
            key={topModule.id}
            className="rounded-lg shadow-sm border overflow-hidden mb-6 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
          >
            {/* Blue header for recommended module */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 px-8 py-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-white/20">
                  <RankIcon className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold text-white">
                      {topModule.name_no}
                    </h2>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-white/20 text-white">
                      Score: {topModule.score}
                    </span>
                  </div>
                  <p className="text-blue-100">
                    Anbefalt modul
                  </p>
                </div>
              </div>
            </div>
            <div className="p-8">

              {topModule.description && (
                <div className="mb-6">
                  <div
                    className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300"
                    dangerouslySetInnerHTML={{ __html: topModule.description }}
                  />
                </div>
              )}

              {topModule.rationale && (
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Anbefalt tilnærming
                  </h3>
                  <div
                    className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300"
                    dangerouslySetInnerHTML={{ __html: topModule.rationale }}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Action Buttons */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-8 mb-6">
        <div className="grid md:grid-cols-3 gap-4">
          {/* Download Work Document */}
          <button
            onClick={handleDownload}
            className="flex items-center justify-center gap-3 px-6 py-4 bg-blue-600 dark:bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
          >
            <Download className="h-5 w-5" />
            Last ned arbeidsdokument
          </button>

          {/* Print Module */}
          <button
            onClick={() => window.print()}
            className="flex items-center justify-center gap-3 px-6 py-4 bg-gray-600 dark:bg-gray-700 text-white rounded-lg font-semibold hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
          >
            <Printer className="h-5 w-5" />
            Skriv ut modul
          </button>

          {/* Continue to Report */}
          <button
            onClick={handleContinueToReport}
            className="flex items-center justify-center gap-3 px-6 py-4 bg-green-600 dark:bg-green-500 text-white rounded-lg font-semibold hover:bg-green-700 dark:hover:bg-green-600 transition-colors"
          >
            Gå til rapport
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-6 border border-blue-100 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Viktig informasjon</h4>
            <p className="text-gray-700 dark:text-gray-300 text-sm">
              Denne modulanbefalingen er basert på den gjennomførte vurderingen.
              Helsepersonell må alltid gjøre egne faglige vurderinger før tiltak iverksettes.
              Ved akutte situasjoner, kontakt helseavdelingen umiddelbart.
            </p>
          </div>
        </div>
      </div>
    </AssessmentLayout>
  );
}
